import { css } from "@emotion/react";
import { type TranslatedValue } from "@swo/storefront";
import { ReadonlyNonEmptyArray } from "fp-ts/lib/ReadonlyNonEmptyArray";
import React from "react";
import isEqual from "react-fast-compare";
import { useCtxFns } from "../../global/page-context";
import { useDeviceScreenWidth } from "../../hooks/use-device-screen-height";
import { PDPCarouselProducts } from "../../layouts/product-detail-page-layout/PDP_AccessoriesCarousel/ProductDetailPageCarousel";
import { ImageCarouselComponentHeadline } from "../_shared-components/ui-elements/SwarovskiGenericCarouselComponent/ImageCarouselComponentHeadline";
import { SwarovskiGenericCarouselComponent } from "../_shared-components/ui-elements/SwarovskiGenericCarouselComponent/SwarovskiGenericCarouselComponent";
import { T } from "../T";
import { useCarouselElements } from "./useCarouselElements";

export type FrontendProductCarouselElem = Omit<PDPCarouselProducts, "price" | "discountedPrice" | "internalLink"> & {
  price: PDPCarouselProducts["price"] | undefined;
  discountedPrice: PDPCarouselProducts["discountedPrice"] | undefined;
  internalLink: PDPCarouselProducts["internalLink"] | undefined;
  isDiscounted: boolean;
};

function _PDPProductsCarouselComponent(props: {
  headline: TranslatedValue | undefined;
  elements: ReadonlyNonEmptyArray<FrontendProductCarouselElem>;
  width: "full" | "content";
  scrollSnap: boolean;
  elementsAlignment?: "to-left" | "centered";
  theme: "normal" | "wide-with-background";
}) {
  const width = useDeviceScreenWidth();
  const { imageWidth, background } = getProps(width);
  const { showDiscountablePrice } = useCtxFns();

  const carouselElements = useCarouselElements(props.elements, showDiscountablePrice, background);

  function getProps(windowWidth: number | undefined) {
    switch (props.theme) {
      case "normal": {
        return {
          imageWidth: 1400 / 4,
          background: "transparent" as const,
        };
      }
      case "wide-with-background": {
        return {
          imageWidth: !windowWidth ? 320 : windowWidth > 450 * 3 ? (1400 - 2 * 25) / 3 : 320,
          background: "grey" as const,
        };
      }
      default: {
        const _: never = props.theme;
        return _;
      }
    }
  }

  const headline = props.headline && (
    <ImageCarouselComponentHeadline className={"u-uppercase"}>
      <T>{props.headline}</T>
    </ImageCarouselComponentHeadline>
  );

  return (
    <SwarovskiGenericCarouselComponent
      key={imageWidth}
      width={props.width}
      headline={headline}
      elementsAlignment={props.elementsAlignment}
      scrollSnap={props.scrollSnap}
      bottomText={undefined}
      carouselElements={carouselElements}
      imageWidth={imageWidth}
      background={background}
      wrapperContainerStyles={css`
        width: 100%;
      `}
    />
  );
}

export const PDPProductsCarouselComponent = React.memo(_PDPProductsCarouselComponent, isEqual);
